import DOMPurify from "isomorphic-dompurify";

export function socialEmbedUrl(value) {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.username || url.password || url.port) return null;
    const host = url.hostname.replace(/^www\./, "");
    if (["twitter.com", "x.com"].includes(host) && /\/status\/\d+/.test(url.pathname)) return { provider: "twitter", url: url.href };
    if (["threads.net", "threads.com"].includes(host) && /\/@[^/]+\/post\/[\w-]+/.test(url.pathname)) return { provider: "threads", url: url.href };
    if (host === "instagram.com" && /^\/(p|reel|tv)\/[\w-]+/.test(url.pathname)) return { provider: "instagram", url: url.href };
    if (["facebook.com", "m.facebook.com"].includes(host)) return { provider: "facebook", url: url.href };
    if (host === "tiktok.com" && /\/video\/\d+/.test(url.pathname)) return { provider: "tiktok", url: url.href };
    if (["youtube.com", "youtu.be"].includes(host)) {
      const id = host === "youtu.be" ? url.pathname.slice(1) : url.searchParams.get("v") || url.pathname.split("/")[2];
      if (/^[\w-]{11}$/.test(id || "")) return { provider: "youtube", url: "https://www.youtube.com/watch?v=" + id };
    }
    if (host === "vimeo.com" && /^\/\d+$/.test(url.pathname)) return { provider: "vimeo", url: url.href };
  } catch { /* Invalid or unsupported URL. */ }
  return null;
}

export const escapeArticleAttribute = (value) => String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export function socialEmbedHtml(value) {
  const embed = socialEmbedUrl(value);
  if (!embed) return null;
  const safe = escapeArticleAttribute(embed.url);
  return '<div data-social-url="' + safe + '" class="article-social-embed"><a href="' + safe + '">View ' + embed.provider + ' post</a></div><p><br></p>';
}

export function safeFrame(value) {
  try {
    const u = new URL(value);
    if (u.protocol !== "https:" || u.username || u.password || u.port) return false;
    return (u.hostname === "www.facebook.com" && /^\/plugins\/(post|video)\.php$/.test(u.pathname)) ||
      (["www.youtube.com", "www.youtube-nocookie.com", "player.vimeo.com", "www.tiktok.com"].includes(u.hostname) && /^\/(embed|video|player)\//.test(u.pathname));
  } catch { return false; }
}

const LINK_REL_TOKENS = new Set(["noopener", "noreferrer", "nofollow", "sponsored", "ugc"]);

// Links keep a new-tab target only as _blank, and a new tab never gets a handle on this page.
function normalizeLink(link) {
  const rel = new Set(String(link.getAttribute("rel") || "").toLowerCase().split(/\s+/).filter(token => LINK_REL_TOKENS.has(token)));
  if (link.getAttribute("target") === "_blank") { rel.add("noopener"); rel.add("noreferrer"); }
  else link.removeAttribute("target");
  if (rel.size) link.setAttribute("rel", [...rel].join(" "));
  else link.removeAttribute("rel");
}

export function normalizeLinks(root) {
  root.querySelectorAll("a").forEach(normalizeLink);
  return root;
}

// Publication pages: DOMPurify's defaults (custom data attributes intact) plus safe new-tab links.
export function sanitizePageHtml(value) {
  const fragment = normalizeLinks(DOMPurify.sanitize(String(value || ""), { RETURN_DOM_FRAGMENT: true, ADD_ATTR: ["target"] }));
  const container = fragment.ownerDocument.createElement("div");
  container.append(fragment);
  return container.innerHTML;
}

// Inline styles from pasted or legacy markup may only format text and size media. Layout
// (position, z-index, viewport units) and editor-only decoration (selection borders) are dropped.
const ALLOWED_STYLE_PROPERTIES = new Set(["text-align", "font-weight", "font-style", "text-decoration", "color", "max-width", "width", "height"]);
const UNSAFE_STYLE_VALUE = /(url\s*\(|expression\s*\(|\d(vw|vh|vmin|vmax)\b|!important)/i;

export function sanitizeInlineStyle(value) {
  return String(value || "").split(";").map(rule => {
    const separator = rule.indexOf(":");
    if (separator < 1) return null;
    const property = rule.slice(0, separator).trim().toLowerCase();
    const propertyValue = rule.slice(separator + 1).trim();
    if (!ALLOWED_STYLE_PROPERTIES.has(property) || !propertyValue || UNSAFE_STYLE_VALUE.test(propertyValue)) return null;
    return property + ":" + propertyValue;
  }).filter(Boolean).join(";");
}

// Preserve provider markup, never publisher-supplied scripts or arbitrary frames.
// deferFrames (public rendering) turns allow-listed iframes into placeholders that load
// only once the reader's consent allows embedded content; stored content keeps the iframe.
export function sanitizeArticleContent(value, { deferFrames = false } = {}) {
  const fragment = DOMPurify.sanitize(String(value || ""), { RETURN_DOM_FRAGMENT: true, ADD_TAGS: ["iframe"], ADD_ATTR: ["allowfullscreen", "frameborder", "target"] });
  normalizeLinks(fragment);
  fragment.querySelectorAll("[data-editor-selected]").forEach(node => node.removeAttribute("data-editor-selected"));
  // Older editors stored captions as the literal "Image Title: X | Credit: Y".
  fragment.querySelectorAll("figcaption").forEach((caption) => {
    const legacy = !caption.children.length && caption.textContent.match(/^\s*Image Title:\s*(.*?)\s*\|\s*Credit:\s*(.*?)\s*$/s);
    if (!legacy) return;
    const doc = caption.ownerDocument;
    caption.replaceChildren();
    if (legacy[1]) caption.append(doc.createTextNode(legacy[1]));
    if (legacy[2]) {
      const credit = doc.createElement("span");
      credit.className = "article-figure-credit";
      credit.textContent = legacy[2];
      if (legacy[1]) caption.append(doc.createTextNode(" "));
      caption.append(credit);
    }
    if (!legacy[1] && !legacy[2]) caption.remove();
    caption.parentElement?.classList?.add("article-figure");
  });
  fragment.querySelectorAll("[style]").forEach((node) => {
    const style = sanitizeInlineStyle(node.getAttribute("style"));
    if (style) node.setAttribute("style", style);
    else node.removeAttribute("style");
  });
  fragment.querySelectorAll(".fb-post, .fb-video").forEach((node) => {
    const embed = socialEmbedUrl(node.getAttribute("data-href"));
    if (embed?.provider === "facebook") node.setAttribute("data-social-url", embed.url);
  });
  fragment.querySelectorAll("iframe").forEach((frame) => {
    if (!safeFrame(frame.getAttribute("src"))) frame.remove();
    else if (deferFrames) {
      const placeholder = fragment.ownerDocument.createElement("div");
      placeholder.setAttribute("data-embed-frame", frame.getAttribute("src"));
      frame.replaceWith(placeholder);
    } else {
      frame.removeAttribute("srcdoc");
      frame.setAttribute("loading", "lazy");
      frame.setAttribute("title", "Embedded social post");
      frame.setAttribute("style", "width:100%;max-width:100%;min-height:400px;border:0");
    }
  });
  const container = fragment.ownerDocument.createElement("div");
  container.append(fragment);
  return container.innerHTML;
}
